import { useNavigate } from "react-router-dom";
import Footer from "../layout/Footer";
import usePageView from "../hooks/usePageView";
import { useSEO } from "../hooks/useSEO";

import playStoreIcon from "../assets/images/svg/mage_playstore.svg";

/* ---------- inline Apple icon ---------- */

const AppleIcon = ({ className = "" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.86-3.08.43-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.42C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

/* ---------- page ---------- */

export default function CreatorsPanel() {
  usePageView("creators-portal");

  useSEO({
    title: "Creators Panel — Vastavik",
    description:
      "Vastavik for creators: register and protect your photos, video and audio, embed QR / owner-key watermarks, and get blockchain-backed proof of authenticity. Available on the web and mobile apps.",
    path: "/creators-panel",
    keywords:
      "Vastavik creators, creator portal, protect media, register content, creator authenticity",
  });

  const navigate = useNavigate();

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col text-black overflow-x-hidden">
      {/* Header */}
      <header className="w-full">
        <div className="max-w-[1280px] mx-auto px-6 md:px-14 pt-5 md:pt-6">
          <span
            onClick={() => navigate("/")}
            className="
              font-maxwell-regular
              text-[24px]
              leading-none
              tracking-[0.03em]
              uppercase
              text-black
              cursor-pointer
              inline-block
            "
          >
            VASTAVIK
          </span>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-start px-6 pb-8 pt-6 md:pt-8 max-w-[1050px] mx-auto w-full">
        <div className="w-full flex flex-col items-center text-center">
          {/* Headline */}
          <h1 className="font-caviar-bold text-[32px] sm:text-[40px] leading-[1.15] tracking-[-0.005em] text-[#131313] mb-6 md:mb-7">
            Prove your content Is Real
            <br className="hidden md:block" /> before the World questions it.
          </h1>

          {/* Main Dark Divider */}
          <hr className="w-full border-t border-black mb-6 md:mb-8" />

          {/* Three Columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0 w-full mb-7 md:mb-8">
            {/* Original */}
            <div className="flex flex-col items-center text-center px-4 md:px-8">
              <h2 className="font-productsans-medium text-[24px] tracking-[-0.005em] text-black mb-2">
                Original
              </h2>

              <div className="w-[91px] h-[1px] bg-[#ADADAD] mb-4" />

              <p className="font-productsans-light text-[18.62px] tracking-[-0.005em] leading-[1.4] text-black">
                Validate your{" "}
                <span className="text-[#DC2323]">videos, photos</span>, and{" "}
                <span className="text-[#DC2323]">audio</span> via Vastavik
                before publishing, so your audience can trust and cannot be
                misled.
              </p>
            </div>

            {/* Protected */}
            <div className="flex flex-col items-center text-center px-4 md:px-8 md:border-l md:border-r border-black/20">
              <h2 className="font-productsans-medium text-[24px] tracking-[-0.005em] text-black mb-2">
                Protected
              </h2>

              <div className="w-[91px] h-[1px] bg-[#ADADAD] mb-4" />

              <p className="font-productsans-light text-[18.62px] tracking-[-0.005em] leading-[1.4] text-black">
                Protect your reputation from deepfake misuse. State that “only{" "}
                <span className="text-[#DC2323]">
                  Vastavik-validated media
                </span>{" "}
                is truly from you, anything else online is not endorsed by
                you”.
              </p>
            </div>

            {/* Trusted */}
            <div className="flex flex-col items-center text-center px-4 md:px-8">
              <h2 className="font-productsans-medium text-[24px] tracking-[-0.005em] text-black mb-2">
                Trusted
              </h2>

              <div className="w-[91px] h-[1px] bg-[#ADADAD] mb-4" />

              <p className="font-productsans-light text-[18.62px] tracking-[-0.005em] leading-[1.4] text-black">
                Built for{" "}
                <span className="text-[#DC2323]">authentic</span> and{" "}
                <span className="text-[#DC2323]">original</span> content
                creators to{" "}
                <span className="text-[#DC2323]">stand apart</span> in the
                AI-generated content age.
              </p>
            </div>
          </div>

          {/* Bottom Paragraph */}
          <p className="font-productsans-light text-[18.62px] tracking-[-0.005em] leading-[1.5] text-black max-w-[728px] mb-7 md:mb-8">
            Vastavik helps creators prove their media is authentic, not
            AI-generated — ending the "
            <span className="text-[#DC2323]">is it real?</span>" debate before
            it starts. It also protects your media and reputation from
            exploitation, misuse and you are constantly discrediting deepfakes
            of you.
          </p>

          {/* Account Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 items-center gap-3 sm:gap-4 w-full max-w-[700px] mb-7 md:mb-8">
            <a
              href="https://creator.vastavik.org/register"
              target="_blank"
              rel="noopener noreferrer"
              className="
                w-full
                h-[48px]
                rounded-[8px]
                bg-black
                text-white
                font-caviar-bold
                text-[20px]
                tracking-[-0.005em]
                inline-flex
                items-center
                justify-center
                hover:opacity-90
                hover:no-underline
                transition
              "
            >
              Create Account
            </a>

            <a
              href="https://creator.vastavik.org/login"
              target="_blank"
              rel="noopener noreferrer"
              className="
                w-full
                h-[48px]
                rounded-[8px]
                bg-white
                text-black
                border
                border-black
                font-caviar-bold
                text-[20px]
                tracking-[-0.005em]
                inline-flex
                items-center
                justify-center
                hover:bg-black
                hover:text-white
                hover:no-underline
                transition
                duration-300
              "
            >
              Login
            </a>
          </div>

          {/* Store Buttons */}
          <div className="flex flex-row flex-wrap items-center justify-center gap-[9px]">
            {/* Apple Store */}
            <a
              href="https://apps.apple.com/in/app/vastavik/id6756848799"
              target="_blank"
              rel="noopener noreferrer"
              className="
                w-[148px]
                h-[50px]
                px-[9px]
                py-[6px]
                border
                border-black/35
                rounded-[5px]
                bg-white
                inline-flex
                items-center
                justify-center
                gap-[9px]
                hover:bg-gray-50
                hover:no-underline
                transition
                shrink-0
              "
            >
              <AppleIcon className="w-[27px] h-[27px] text-black shrink-0" />

              <div className="text-left leading-none">
                <span className="block font-productsans-light text-[10px] text-black mb-[3px]">
                  Download on
                </span>

                <span className="block font-productsans-bold text-[16px] tracking-[-0.005em] text-black whitespace-nowrap">
                  Apple Store
                </span>
              </div>
            </a>

            {/* Play Store */}
            <a
              href="https://play.google.com/store/apps/details?id=com.vastavik&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="
                w-[148px]
                h-[50px]
                px-[9px]
                py-[6px]
                border
                border-black/35
                rounded-[5px]
                bg-white
                inline-flex
                items-center
                justify-center
                gap-[9px]
                hover:bg-gray-50
                hover:no-underline
                transition
                shrink-0
              "
            >
              <img
                src={playStoreIcon}
                alt=""
                aria-hidden="true"
                className="
                  w-[27px]
                  h-[27px]
                  object-contain
                  shrink-0
                "
              />

              <div className="text-left leading-none">
                <span className="block font-productsans-light text-[10px] text-black mb-[3px]">
                  Get it on
                </span>

                <span className="block font-productsans-bold text-[16px] tracking-[-0.005em] text-black whitespace-nowrap">
                  Play Store
                </span>
              </div>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}